import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { DocRenderer } from '@cyntler/react-doc-viewer';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import ViewerToolbar, { ViewMode } from './ViewerToolbar';
import { useViewerContainer } from './ViewerContainerContext';

// Fix pdfjs worker URL - react-doc-viewer's getMetaURL() resolves to a file:// path in CRA/webpack builds
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Options for react-pdf Document to load standard fonts and cmaps for complex PDFs (e.g. IRS W-9).
const PDF_OPTIONS = {
  cMapUrl: '/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: '/standard_fonts/',
};

const PdfViewer: DocRenderer = ({ mainState: { currentDocument } }) => {
  const viewerContext = useViewerContainer();
  const containerWidth = viewerContext.width;
  const containerHeight = viewerContext.height;
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [zoom, setZoom] = useState<number>(800);
  const [viewMode, setViewMode] = useState<ViewMode>(viewerContext.initialViewMode || 'fit-width');
  const pageDimensions = useRef<Map<number, { width: number; height: number }>>(new Map());
  // Force re-render when dimensions are first captured for the current page
  const [, setDimVersion] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const wheelAccum = useRef<number>(0);
  const wheelTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Measure toolbar height once on mount
  useEffect(() => {
    if (containerRef.current?.previousElementSibling) {
      setToolbarHeight(containerRef.current.previousElementSibling.getBoundingClientRect().height);
    }
  }, []);

  const scrollAreaHeight = containerHeight - (toolbarHeight || 40);

  const handlePageLoad = useCallback((page: any) => {
    const pageNum = page.pageNumber || page._pageIndex + 1;
    pageDimensions.current.set(pageNum, { width: page.originalWidth, height: page.originalHeight });
    setDimVersion(v => v + 1);
  }, []);

  const goToPage = useCallback((newPage: number, direction: 'up' | 'down') => {
    setSlideDirection(direction);
    setTimeout(() => {
      setCurrentPage(newPage);
      setSlideDirection(null);
    }, 200);
  }, []);

  const navigatePage = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > numPages) return;
    const direction = newPage > currentPage ? 'up' : 'down';
    if (viewMode === 'single-page') {
      goToPage(newPage, direction);
    } else {
      // Just scroll — the IntersectionObserver will update currentPage
      const el = containerRef.current?.querySelector(`[data-page="${newPage}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [viewMode, numPages, currentPage, goToPage]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (viewMode !== 'single-page' || numPages <= 1) return;
    wheelAccum.current += e.deltaY;
    if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
    wheelTimeout.current = setTimeout(() => { wheelAccum.current = 0; }, 300);

    const threshold = 100;
    if (wheelAccum.current > threshold) {
      wheelAccum.current = 0;
      setCurrentPage(p => {
        if (p >= numPages) return p;
        goToPage(p + 1, 'up');
        return p;
      });
    } else if (wheelAccum.current < -threshold) {
      wheelAccum.current = 0;
      setCurrentPage(p => {
        if (p <= 1) return p;
        goToPage(p - 1, 'down');
        return p;
      });
    }
  }, [viewMode, numPages, goToPage]);

  const currentPageDims = pageDimensions.current.get(currentPage);
  const currentNativeWidth = currentPageDims?.width || 612; // default letter width in PDF points
  const currentNativeHeight = currentPageDims?.height || 792;
  const currentPageAspect = currentNativeWidth / currentNativeHeight;

  const availableWidth = containerWidth - 2;
  let pageWidth: number;
  if (viewMode === 'fit-width') {
    pageWidth = availableWidth;
  } else if (viewMode === 'single-page') {
    const widthConstrained = availableWidth;
    const heightConstrained = (scrollAreaHeight - 2) * currentPageAspect;
    pageWidth = Math.min(widthConstrained, heightConstrained);
  } else {
    pageWidth = zoom;
  }

  const nativeWidthAtScreenDpi = currentNativeWidth * (96 / 72);
  const displayZoomPct = nativeWidthAtScreenDpi > 0 ? Math.round((pageWidth / nativeWidthAtScreenDpi) * 100) : 100;

  const isSinglePage = viewMode === 'single-page';

  const zoomStep = nativeWidthAtScreenDpi > 0 ? nativeWidthAtScreenDpi * 0.25 : 50;
  const handleZoomOut = () => {
    const newWidth = Math.max(nativeWidthAtScreenDpi * 0.25, pageWidth - zoomStep);
    setZoom(newWidth);
    handleSetViewMode('zoom');
  };
  const handleZoomIn = () => {
    const newWidth = Math.min(nativeWidthAtScreenDpi * 5, pageWidth + zoomStep);
    setZoom(newWidth);
    handleSetViewMode('zoom');
  };
  // When leaving single-page mode, scroll to the page that was being viewed
  const pendingScrollPage = useRef<number | null>(null);

  const handleSetViewMode = useCallback((mode: ViewMode) => {
    if (viewMode === 'single-page' && mode !== 'single-page') {
      // Save current page so we can scroll to it after multi-page render
      pendingScrollPage.current = currentPage;
    }
    setViewMode(mode);
  }, [viewMode, currentPage]);
  const handleSetZoomPct = (pct: number) => {
    const newWidth = nativeWidthAtScreenDpi * (pct / 100);
    setZoom(newWidth);
    handleSetViewMode('zoom');
  };

  // After switching from single-page to multi-page, scroll to the page that was visible
  useEffect(() => {
    if (isSinglePage || !containerRef.current || pendingScrollPage.current === null) return;
    const page = pendingScrollPage.current;
    pendingScrollPage.current = null;
    // Use requestAnimationFrame to wait for pages to render
    requestAnimationFrame(() => {
      const el = containerRef.current?.querySelector(`[data-page="${page}"]`);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
  }, [isSinglePage]);

  // Track visible page in fit-width/zoom modes via IntersectionObserver
  useEffect(() => {
    if (isSinglePage || !containerRef.current || numPages <= 1) return;

    // Track ratios for all pages so we always pick from the full picture
    const ratios = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const page = parseInt((entry.target as HTMLElement).dataset.page || '0', 10);
          if (page > 0) {
            ratios.set(page, entry.isIntersecting ? entry.intersectionRatio : 0);
          }
        }
        // Pick page with highest ratio across all tracked pages
        let bestPage = 0;
        let bestRatio = 0;
        for (const [page, ratio] of ratios) {
          if (ratio > bestRatio) {
            bestPage = page;
            bestRatio = ratio;
          }
        }
        if (bestPage > 0) setCurrentPage(bestPage);
      },
      { root: containerRef.current, threshold: [0, 0.25, 0.5, 0.75, 1.0] }
    );

    const pages = containerRef.current.querySelectorAll('[data-page]');
    pages.forEach(p => observer.observe(p));

    return () => observer.disconnect();
  }, [isSinglePage, numPages]);

  if (!currentDocument?.uri && !currentDocument?.fileData) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: `${containerHeight}px` }}>
      <ViewerToolbar
        displayZoomPct={displayZoomPct}
        viewMode={viewMode}
        numPages={numPages}
        currentPage={currentPage}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onSetZoomPct={handleSetZoomPct}
        onSetViewMode={handleSetViewMode}
        onNavigatePage={navigatePage}
        extra={viewerContext.toolbarExtra}
      />
      <div
        ref={containerRef}
        onWheel={handleWheel}
        style={{
          overflow: isSinglePage ? 'hidden' : 'auto',
          flex: 1,
          background: '#e9ecef',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSinglePage ? 'center' : undefined,
          justifyContent: isSinglePage ? 'center' : undefined,
        }}
      >
        <Document
          file={currentDocument.fileData ?? currentDocument.uri}
          options={PDF_OPTIONS}
          onLoadSuccess={({ numPages: n }) => { setNumPages(n); setCurrentPage(1); }}
          onLoadError={(err) => console.error('PDF load error:', err)}
          loading={<div className="text-center py-5"><Spinner animation="border" size="sm" /> Loading PDF...</div>}
        >
          {isSinglePage ? (
            <div style={{
              transition: slideDirection ? 'transform 0.2s ease-out, opacity 0.2s ease-out' : undefined,
              transform: slideDirection === 'up' ? 'translateY(-100%)' : slideDirection === 'down' ? 'translateY(100%)' : 'translateY(0)',
              opacity: slideDirection ? 0 : 1,
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            }}>
              <Page pageNumber={currentPage} width={pageWidth} renderAnnotationLayer={false} onLoadSuccess={handlePageLoad} />
            </div>
          ) : (
            Array.from({ length: numPages }, (_, i) => (
              <React.Fragment key={i + 1}>
                {i > 0 && <div style={{ height: '8px', flexShrink: 0 }} />}
                <div data-page={i + 1} style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.2)', flexShrink: 0, margin: '0 auto' }}>
                  <Page pageNumber={i + 1} width={pageWidth} renderAnnotationLayer={false} onLoadSuccess={handlePageLoad} />
                </div>
              </React.Fragment>
            ))
          )}
        </Document>
      </div>
    </div>
  );
};
PdfViewer.fileTypes = ['pdf', 'application/pdf'];
PdfViewer.weight = 2;

export default PdfViewer;
